import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  PhoneAndroid as PhoneIcon,
  Security as SecurityIcon,
  Sms as SmsIcon
} from '@mui/icons-material';
import { twoFactorService } from '../services/twoFactorService';
import logger from '../utils/logger';

/**
 * Dialog shown during login when MFA verification is required
 */
const MFAVerificationDialog = ({ open, onClose, mfaResolver, onSuccess }) => {
  const [step, setStep] = useState('select'); // 'select' | 'verify'
  const [selectedHintIndex, setSelectedHintIndex] = useState(0);
  const [verificationCode, setVerificationCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [codeSent, setCodeSent] = useState(false);

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setStep('select');
      setVerificationCode('');
      setError('');
      setCodeSent(false);

      // If only one factor, auto-select and send code
      if (mfaResolver?.hints?.length === 1) {
        handleSelectFactor(0);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleSelectFactor = async (index) => {
    setSelectedHintIndex(index);
    setLoading(true);
    setError('');

    try {
      await twoFactorService.sendMFACode(
        mfaResolver.resolver,
        index,
        'mfa-recaptcha-container'
      );
      setStep('verify');
      setCodeSent(true);
    } catch (err) {
      logger.error('Error sending MFA code:', err);
      setError(err.message || 'Failed to send verification code');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      setError('Please enter the 6-digit verification code');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const userCredential = await twoFactorService.completeMFASignIn(
        mfaResolver.resolver,
        verificationCode
      );

      if (onSuccess) {
        onSuccess(userCredential);
      }
    } catch (err) {
      logger.error('Error verifying MFA code:', err);
      setError(err.message || 'Invalid verification code');
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setLoading(true);
    setError('');

    try {
      await twoFactorService.sendMFACode(
        mfaResolver.resolver,
        selectedHintIndex,
        'mfa-recaptcha-container'
      );
      setError('');
      setCodeSent(true);
    } catch (err) {
      logger.error('Error resending code:', err);
      setError(err.message || 'Failed to resend code');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    twoFactorService.clearRecaptcha();
    setVerificationCode('');
    setError('');
    onClose();
  };

  const renderFactorSelection = () => (
    <Box>
      <Typography variant="body1" paragraph>
        Your account is protected with two-factor authentication.
        Select a verification method to continue signing in.
      </Typography>

      <List>
        {mfaResolver?.hints?.map((hint, index) => (
          <ListItem key={hint.uid} disablePadding>
            <ListItemButton
              onClick={() => handleSelectFactor(index)}
              disabled={loading}
            >
              <ListItemIcon>
                <PhoneIcon color="primary" />
              </ListItemIcon>
              <ListItemText
                primary={hint.displayName || 'Phone'}
                secondary={hint.phoneNumber || 'SMS verification'}
              />
              {loading && selectedHintIndex === index && (
                <CircularProgress size={24} />
              )}
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Box>
  );

  const renderVerification = () => {
    const selectedHint = mfaResolver?.hints?.[selectedHintIndex];

    return (
      <Box>
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <SmsIcon color="primary" />
          <Typography variant="body1">
            Enter the verification code sent to {selectedHint?.phoneNumber || 'your phone'}
          </Typography>
        </Box>

        <TextField
          fullWidth
          label="Verification Code"
          value={verificationCode}
          onChange={(e) => {
            const value = e.target.value.replace(/\D/g, '').slice(0, 6);
            setVerificationCode(value);
          }}
          placeholder="123456"
          disabled={loading}
          autoFocus
          sx={{ mb: 2 }}
          inputProps={{
            maxLength: 6,
            style: { letterSpacing: '0.5em', textAlign: 'center', fontSize: '1.5em' }
          }}
          helperText="Check your text messages for the 6-digit code"
        />

        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Button
            variant="text"
            size="small"
            onClick={handleResendCode}
            disabled={loading}
          >
            Resend Code
          </Button>
          {mfaResolver?.hints?.length > 1 && (
            <Button
              variant="text"
              size="small"
              onClick={() => setStep('select')}
              disabled={loading}
            >
              Use Different Method
            </Button>
          )}
        </Box>
      </Box>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={loading}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <SecurityIcon color="primary" />
          Two-Factor Verification
        </Box>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {codeSent && step === 'verify' && !error && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Verification code sent!
          </Alert>
        )}

        {step === 'select' && renderFactorSelection()}
        {step === 'verify' && renderVerification()}

        {/* Hidden reCAPTCHA container */}
        <div id="mfa-recaptcha-container" />
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        {step === 'verify' && (
          <Button
            variant="contained"
            onClick={handleVerifyCode}
            disabled={loading || verificationCode.length !== 6}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            {loading ? 'Verifying...' : 'Verify & Sign In'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default MFAVerificationDialog;
